import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  PatientAPCMConsentCreate,
  PatientAPCMConsentResponse,
} from "@/lib/types";
import api from "@/lib/api";
import { useOrganizationsStore } from "./organizationsStore";
import { timestampMs } from "@/lib/datetime";

// Helper function to format consent status
export const formatConsentStatus = (
  status: string | null | undefined
): string => {
  if (!status) return "None";

  // Convert from snake_case or ALL_CAPS to Title Case
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

interface ConsentsState {
  consents: PatientAPCMConsentResponse[];
  consentsById: Record<string, PatientAPCMConsentResponse[]>; // patientId -> consents
  isLoading: boolean;
  error: string | null;
  // Get all consents for an organization
  fetchOrganizationConsents: (organizationId: string) => Promise<void>;
  // Get consents for current organization
  fetchConsentsForCurrentOrg: () => Promise<void>;
  // Get consents for a specific patient in the organization
  fetchPatientConsents: (
    organizationId: string,
    patientId: string
  ) => Promise<PatientAPCMConsentResponse[]>;
  // Create a new consent
  createConsent: (
    consentData: PatientAPCMConsentCreate
  ) => Promise<PatientAPCMConsentResponse>;
  // Get the latest consent for a patient
  getLatestConsentForPatient: (
    patientId: string
  ) => PatientAPCMConsentResponse | null;
  // Check if a patient has an active consent
  hasActiveConsent: (patientId: string) => boolean;
  // Clear consents (e.g., on logout)
  clearConsents: () => void;
}

export const useConsentsStore = create<ConsentsState>()(
  immer<ConsentsState>((set, get) => ({
    consents: [],
    consentsById: {},
    isLoading: false,
    error: null,

    fetchOrganizationConsents: async (organizationId: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const consents = await api.getOrganizationConsents(organizationId);

        // Create a map of patient ID -> consents
        const consentsById: Record<string, PatientAPCMConsentResponse[]> = {};

        for (const consent of consents) {
          if (!consentsById[consent.patient_id]) {
            consentsById[consent.patient_id] = [];
          }
          consentsById[consent.patient_id].push(consent);
        }

        set((state) => {
          state.consents = consents;
          state.consentsById = consentsById;
          state.isLoading = false;
        });
      } catch (error) {
        console.error("Failed to fetch consents:", error);
        set((state) => {
          state.error = "Failed to fetch patient consents";
          state.isLoading = false;
        });
      }
    },

    fetchConsentsForCurrentOrg: async () => {
      const selectedOrgId =
        useOrganizationsStore.getState().selectedOrganizationId;

      if (!selectedOrgId) {
        set((state) => {
          state.error = "No organization selected";
        });
        return;
      }

      await get().fetchOrganizationConsents(selectedOrgId);
    },

    fetchPatientConsents: async (organizationId: string, patientId: string) => {
      try {
        const consents = await api.getPatientOrganizationConsents(
          organizationId,
          patientId
        );

        // Update the store with these consents
        set((state) => {
          state.consentsById[patientId] = consents;

          // Also update the full consent list
          const filteredConsents = state.consents.filter(
            (c) => c.patient_id !== patientId
          );
          state.consents = [...filteredConsents, ...consents];
        });

        return consents;
      } catch (error) {
        console.error(
          `Failed to fetch consents for patient ${patientId}:`,
          error
        );
        return [];
      }
    },

    createConsent: async (consentData) => {
      try {
        const newConsent = await api.createPatientConsent(consentData);

        // Update the store with the new consent
        set((state) => {
          state.consents.push(newConsent);

          if (!state.consentsById[newConsent.patient_id]) {
            state.consentsById[newConsent.patient_id] = [];
          }

          state.consentsById[newConsent.patient_id].push(newConsent);
        });

        return newConsent;
      } catch (error) {
        console.error("Failed to create consent:", error);
        throw error;
      }
    },

    getLatestConsentForPatient: (patientId: string) => {
      const { consentsById } = get();
      const patientConsents = consentsById[patientId] || [];

      if (patientConsents.length === 0) return null;

      // Sort by creation date and return the most recent
      return [...patientConsents].sort(
        (a, b) =>
          timestampMs(b.created_at) - timestampMs(a.created_at)
      )[0];
    },

    hasActiveConsent: (patientId: string) => {
      const latestConsent = get().getLatestConsentForPatient(patientId);
      return latestConsent ? latestConsent.consent_status === "active" : false;
    },

    clearConsents: () => {
      set((state) => {
        state.consents = [];
        state.consentsById = {};
        state.error = null;
      });
    },
  }))
);
